<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/http.js'
import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import { reportError } from '@/composables/useSave.js'
import { dictionarySchema, fromDictionary, dictionaryBody } from '@/lib/dictionaries.js'
import UiDrawer from '@/components/ui/UiDrawer.vue'
import UiButton from '@/components/ui/UiButton.vue'
import SchemaForm from '@/components/settings/SchemaForm.vue'

/**
 * A dictionary edited — or a new one made — where it is used (D-136): from the badge by a
 * field's label, or from "+ New dictionary" by a custom field. A panel from the right; the
 * saved dictionary is handed back (`saved`).
 */
const props = defineProps({ open: Boolean, dictionaryKey: { type: String, default: '' } })
const emit = defineEmits(['close', 'saved'])
const { t } = useI18n()
const meta = useMeta()
const ui = useUi()
const form = ref(null)
const uuid = ref(null)
const errors = ref({})
const busy = ref(false)
const schema = dictionarySchema()
let start = ''

watch(() => [props.open, props.dictionaryKey], async ([open]) => {
  if (!open) return
  errors.value = {}
  uuid.value = props.dictionaryKey ? meta.dictionary(props.dictionaryKey).uuid || null : null
  form.value = null
  try {
    form.value = uuid.value ? fromDictionary((await api(`/config/dictionaries/${uuid.value}`)).data) : { key: '', label: { pl: '', en: '' }, entries: [] }
  } catch (e) {
    reportError(e)
    return emit('close')
  }
  start = JSON.stringify(form.value)
}, { immediate: true })

async function close() {
  if (form.value && JSON.stringify(form.value) !== start && !(await ui.ask({ title: t('create.discardTitle'), message: t('create.discardMessage'), confirmLabel: t('create.discard'), danger: true }))) return
  emit('close')
}
async function save() {
  busy.value = true
  errors.value = {}
  try {
    const body = dictionaryBody(form.value, !uuid.value)
    const saved = (await api(uuid.value ? `/config/dictionaries/${uuid.value}` : '/config/dictionaries', { method: uuid.value ? 'PATCH' : 'POST', body })).data
    await meta.load({ force: true })
    ui.success(t('messages.saved'))
    emit('saved', saved)
  } catch (e) {
    if (e?.status === 422 && Object.keys(e.errors || {}).length) errors.value = e.errors
    else reportError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiDrawer :open="open" :title="uuid ? meta.localized(form?.label) || $t('settings.dictionaries') : $t('dictionaries.new')" width="max-w-3xl" @close="close">
    <form v-if="form" class="p-4" data-testid="dictionary-editor" @submit.prevent="save">
      <SchemaForm v-model="form" :schema="schema" :errors="errors" :creating="!uuid" />
    </form>
    <template #footer>
      <div class="flex justify-end gap-2">
        <UiButton @click="close">{{ $t('common.cancel') }}</UiButton>
        <UiButton variant="primary" icon="fa-check" :loading="busy" data-testid="dictionary-save" @click="save">{{ $t('common.save') }}</UiButton>
      </div>
    </template>
  </UiDrawer>
</template>
